// utils/units/registry.js
//
// The measurement registry: measures, units, conversion factors and parser
// spellings for the `measurement` element type.
//
// DEPENDENCY-FREE AND PLATFORM-FREE ON PURPOSE. Every file in utils/units/ is
// vendored verbatim into keepr-web (`src/utils/units/vendored/`) and kept in
// lock-step by the shared test-vector file next to it (`vectors.json`), which
// both repos' suites drive. Nothing here may require() anything outside this
// folder -- no Mongo, no Hapi, no Boom, no dayjs.
//
// Shapes (see docs/plans/measurements.md, "Shared module contract"):
//
//   measure: { id, label, canonical, allowNegative, allowZero,
//              systemDefaults: { metric, imperial },
//              familyDefaults: { [family]: { metric, imperial } } | null,
//              rateOf: measureId | null,
//              units: [unitId, ...] }
//   unit:    { id, measure, label, symbol, system: 'metric'|'imperial'|'any',
//              family, aliases: [...], toBase(n), fromBase(n), convertible,
//              autoDisplay, decreasing, joined,
//              parts?: [majorId, minorId], minorPerMajor?,
//              numerator?: unitId, per?: unitId }
//
// `decreasing: true` marks a RECIPROCAL unit, whose number runs against its
// base: fuel economy's canonical unit is km/L, and 8 L/100km is 12.5 km/L
// while 10 L/100km is 10. Every comparison downstream is made on `base`, so a
// bound written in such a unit turns around on the way there -- "under
// 8 L/100km" is "base above 12.5" -- and every caller that compares a literal
// in a unit against a stored base asks convert.js `baseOperator`. `joined`
// prints the symbol against the number ("45.0°"), as a plane angle is
// written; every other unit prints with a space. `allowZero: false` refuses a
// zero on the write path: 0 L/100km has no base at all, and 0 mpg would
// print as nothing in litres per 100 km.
//
// A RATE measure (`rateOf` set) is an amount of another measure per unit of
// time, and every one of its units carries the `numerator` and `per` it is
// made of: 'mL/h' is { numerator: 'mL', per: 'h' }. See "rates" below.
//
// `convertible: false` marks a unit that shares a measure with others but
// converts to none of them: tablets, puffs, drops, international units. They
// are COUNTS of a thing, and "3 tablets in puffs" is not a rounding question,
// it is a category error. They live in one measure so the picker stays one
// list; convert() refuses to move a value between any two of them.
//
// `family` is what KIND of quantity spelling a unit is WITHIN its measure,
// where `system` is which measurement system a viewer preference maps to.
// One `volume` measure holds a tank in gallons and a pile in cubic yards --
// they genuinely convert -- but nothing should ever RE-EXPRESS one as the
// other behind the reader's back, so `auto` display and the unitSystem
// preference both stay inside the family of the element's own default unit
// (format.js). Volume, energy, pressure and data-size split; every unit of a
// measure that does not is family 'default', and a measure with one family
// behaves exactly as it always did.
// `familyDefaults[family]` is the per-family answer to "what does this
// element read as for a metric/imperial viewer"; `systemDefaults` is the
// fallback for a family that has no entry (US dry has no metric spelling).
// A measure whose two systemDefaults name the SAME unit has no metric /
// imperial split at all (seconds, bytes, calories, tallies): the preference
// has nothing to say about it, so it needs no familyDefaults.
//
// `autoDisplay: false` marks a unit people ENTER but should not have chosen
// FOR them: nobody wants "0.25 L" printed back as "2.5 dL", and no existing
// element's automatic display may move because the registry grew. Adding a
// unit to an existing family is therefore always autoDisplay:false -- the
// flag is what makes this whole file's growth provably invisible to stored
// data, whose `base` was computed at save time and is never recomputed. A
// unit is always a candidate for its OWN element's default unit.
//
// The flag is NOT a containment: it stops a unit being chosen, not `auto`
// leaving an entry-only default unit for a larger unit that IS eligible.
// Only `family` contains. A new unit that would read absurdly in the biggest
// auto-eligible unit of the family it lands in needs a family of its own --
// which is why kWh, GiB and hPa have one and cL, which is honestly read back
// as mL or L, does not.
//
// A FACTOR IS A STORAGE FORMAT, NOT A CONSTANT TO BE REFINED. A stored
// measurement carries { value, unit, base }; `base` is computed once, at save
// time, and is never recomputed. Every comparison downstream is numeric on
// that `base` inside an ABSOLUTE window of 1e-9 canonical units -- KQL
// equality and range bounds (utils/kql/compileValues.js BASE_EPSILON), the
// automation and notification matcher (utils/automations/conditions.js),
// and so every filtered grant, dynamic group and saved filter compiled
// through them. Moving a factor by more than that orphans every value
// already saved in the unit: the row still reads "140 mmHg" on screen and
// silently stops matching `= 140 mmHg`, `>= 140 mmHg` and every rule that
// tests it. Six-figure roundings in this file (kn, psi, mmHg, and inHg
// through it) are therefore KEPT, exactly as they shipped; correcting one is
// a data migration, not an edit, and test/units.test.js pins them all.
//
// Every measure has ONE fixed canonical unit (kg, m, degrees C, L, s, ...);
// `toBase`/`fromBase` convert between a unit and that canonical unit. A
// composite unit (lb-oz, ft-in, h-min, ...) stores its magnitude as a decimal
// of its MAJOR part and converts through it.
//
// Aliases are the spellings the parser accepts, matched case-insensitively;
// every entry here is lowercase. Unit ids and symbols are aliases too.

'use strict';

const MEASURE_LIST = [];
const MEASURES = {};
const UNITS = {};
const ALIASES = {};

function defineMeasure(def) {
	const measure = {
		id: def.id,
		label: def.label,
		canonical: def.canonical,
		allowNegative: !!def.allowNegative,
		allowZero: def.allowZero !== false,
		systemDefaults: def.systemDefaults,
		familyDefaults: def.familyDefaults || null,
		rateOf: def.rateOf || null,
		units: [],
	};
	MEASURES[measure.id] = measure;
	MEASURE_LIST.push(measure);
	return measure;
}

function registerAliases(unit, spellings) {
	const seen = [];
	for (const raw of spellings) {
		const spelling = String(raw).toLowerCase();
		if (seen.indexOf(spelling) !== -1) continue;
		seen.push(spelling);
		if (Object.prototype.hasOwnProperty.call(ALIASES, spelling) && ALIASES[spelling] !== unit.id) {
			throw new Error('duplicate unit alias "' + spelling + '" (' + ALIASES[spelling] + ' vs ' + unit.id + ')');
		}
		ALIASES[spelling] = unit.id;
	}
	return seen;
}

function addUnit(unit) {
	if (UNITS[unit.id]) throw new Error('duplicate unit id "' + unit.id + '"');
	if (!MEASURES[unit.measure]) throw new Error('unit "' + unit.id + '" names unknown measure "' + unit.measure + '"');
	UNITS[unit.id] = unit;
	MEASURES[unit.measure].units.push(unit.id);
	return unit;
}

// A unit that is a fixed multiple of the canonical unit.
function scaled(def) {
	const factor = def.factor;
	const unit = {
		id: def.id,
		measure: def.measure,
		label: def.label,
		symbol: def.symbol || def.id,
		system: def.system || 'any',
		family: def.family || 'default',
		aliases: null,
		// Convertible unless the definition says otherwise: every unit that
		// existed before counts did is a multiple of its canonical unit.
		convertible: def.convertible !== false,
		autoDisplay: def.autoDisplay !== false,
		decreasing: false,
		joined: def.joined === true,
		toBase: n => n * factor,
		fromBase: b => b / factor,
	};
	// A unit printed as a word that takes a plural ("2.0 pinches", "1 pinch",
	// KPR-1109): its symbol is the singular. Only the units that say so carry
	// the key, so every unit that existed before prints exactly as it did.
	if (typeof def.plural === 'string' && def.plural) unit.plural = def.plural;
	unit.aliases = registerAliases(unit, [unit.id, unit.symbol].concat(def.plural ? [def.plural] : [], def.aliases || []));
	return addUnit(unit);
}

// A unit with its own conversion functions (temperature is affine).
function custom(def) {
	const unit = {
		id: def.id,
		measure: def.measure,
		label: def.label,
		symbol: def.symbol || def.id,
		system: def.system || 'any',
		family: def.family || 'default',
		aliases: null,
		convertible: def.convertible !== false,
		autoDisplay: def.autoDisplay !== false,
		decreasing: false,
		joined: false,
		toBase: def.toBase,
		fromBase: def.fromBase,
	};
	unit.aliases = registerAliases(unit, [unit.id, unit.symbol].concat(def.aliases || []));
	return addUnit(unit);
}

// A two-part unit whose magnitude is a decimal of its major part.
function composite(def) {
	const major = UNITS[def.parts[0]];
	const minor = UNITS[def.parts[1]];
	if (!major || !minor) throw new Error('composite "' + def.id + '" names an unknown part');
	if (major.measure !== def.measure || minor.measure !== def.measure) {
		throw new Error('composite "' + def.id + '" mixes measures');
	}
	const unit = {
		id: def.id,
		measure: def.measure,
		label: def.label,
		symbol: def.id,
		system: def.system || 'any',
		family: def.family || major.family,
		aliases: null,
		convertible: def.convertible !== false,
		autoDisplay: def.autoDisplay !== false,
		decreasing: false,
		joined: false,
		parts: [major.id, minor.id],
		minorPerMajor: def.minorPerMajor,
		toBase: n => major.toBase(n),
		fromBase: b => major.fromBase(b),
	};
	unit.aliases = registerAliases(unit, [unit.id].concat(def.aliases || []));
	return addUnit(unit);
}

// A unit that is a fixed multiple of the RECIPROCAL of the canonical unit:
// L/100km is 100 / (km/L). Its own function pair, as temperature's affine
// units have, so convert() needs nothing new and no factor-only unit is
// touched -- but its number runs against its base, which `decreasing` tells
// every comparison (see the header). Zero has no reciprocal: the measure
// holding one says allowZero:false, and the pair answers Infinity for it,
// which every reader already treats as "no value" (formatInUnit prints '',
// normalize refuses a base that is not finite).
function reciprocal(def) {
	const factor = def.factor;
	const unit = {
		id: def.id,
		measure: def.measure,
		label: def.label,
		symbol: def.symbol || def.id,
		system: def.system || 'any',
		family: def.family || 'default',
		aliases: null,
		convertible: true,
		autoDisplay: def.autoDisplay !== false,
		decreasing: true,
		joined: false,
		toBase: n => factor / n,
		fromBase: b => factor / b,
	};
	if (MEASURES[def.measure] && MEASURES[def.measure].allowZero !== false) {
		throw new Error('reciprocal unit "' + def.id + '" needs a measure that refuses zero');
	}
	unit.aliases = registerAliases(unit, [unit.id, unit.symbol].concat(def.aliases || []));
	return addUnit(unit);
}

// ------------------------------------------------------------------ mass (kg)

const LB_KG = 0.45359237;

defineMeasure({
	id: 'mass',
	label: 'Mass',
	canonical: 'kg',
	allowNegative: false,
	systemDefaults: { metric: 'kg', imperial: 'lb' },
});
// 'mcg', not the micro sign: clinical practice writes mcg precisely because
// a handwritten or badly-rendered mu is misread as an m, and this registry is
// ASCII-only besides.
scaled({ id: 'mcg', measure: 'mass', label: 'Micrograms', symbol: 'mcg', system: 'metric', factor: 1e-9, aliases: ['ug', 'microgram', 'micrograms'] });
scaled({ id: 'mg', measure: 'mass', label: 'Milligrams', system: 'metric', factor: 1e-6, aliases: ['milligram', 'milligrams'] });
scaled({ id: 'g', measure: 'mass', label: 'Grams', system: 'metric', factor: 1e-3, aliases: ['gm', 'gram', 'grams'] });
scaled({ id: 'kg', measure: 'mass', label: 'Kilograms', system: 'metric', factor: 1, aliases: ['kgs', 'kilo', 'kilos', 'kilogram', 'kilograms'] });
scaled({ id: 't', measure: 'mass', label: 'Metric tons', system: 'metric', factor: 1000, aliases: ['tonne', 'tonnes', 'metric ton', 'metric tons'] });
scaled({ id: 'oz', measure: 'mass', label: 'Ounces', system: 'imperial', factor: LB_KG / 16, aliases: ['ounce', 'ounces'] });
scaled({ id: 'lb', measure: 'mass', label: 'Pounds', system: 'imperial', factor: LB_KG, aliases: ['lbs', 'pound', 'pounds'] });
scaled({ id: 'st', measure: 'mass', label: 'Stone', system: 'imperial', factor: 6.35029318, aliases: ['stone', 'stones'] });
scaled({ id: 'ton', measure: 'mass', label: 'US tons', system: 'imperial', factor: 907.18474, aliases: ['tons', 'short ton', 'short tons'] });
composite({ id: 'lb-oz', measure: 'mass', label: 'Pounds and ounces', system: 'imperial', parts: ['lb', 'oz'], minorPerMajor: 16 });
composite({ id: 'st-lb', measure: 'mass', label: 'Stone and pounds', system: 'imperial', parts: ['st', 'lb'], minorPerMajor: 14 });
// Everything below is an ADDITION to a measure people already store values
// in, so every one of them is autoDisplay:false -- a grain is bigger than a
// milligram and a long ton bigger than a US ton, and `auto` must go on
// printing exactly what it printed yesterday.
scaled({ id: 'gr', measure: 'mass', label: 'Grains', system: 'imperial', factor: 0.00006479891, autoDisplay: false, aliases: ['grain', 'grains'] });
scaled({ id: 'dr', measure: 'mass', label: 'Drams', system: 'imperial', factor: 0.0017718451953125, autoDisplay: false, aliases: ['dram', 'drams'] });
scaled({ id: 'longton', measure: 'mass', label: 'Long tons (UK)', symbol: 'long ton', system: 'imperial', factor: 1016.0469088, autoDisplay: false, aliases: ['long tons', 'imperial ton', 'imperial tons', 'uk ton', 'uk tons'] });
// Troy and gemstone weights: a different tradition, in the same measure
// because they do convert. system 'any' keeps them out of both the metric
// and the imperial auto candidate sets.
scaled({ id: 'ozt', measure: 'mass', label: 'Troy ounces', symbol: 'ozt', system: 'any', factor: 0.0311034768, autoDisplay: false, aliases: ['troy ounce', 'troy ounces'] });
scaled({ id: 'dwt', measure: 'mass', label: 'Pennyweights', symbol: 'dwt', system: 'any', factor: 0.00155517384, autoDisplay: false, aliases: ['pennyweight', 'pennyweights'] });
scaled({ id: 'ct', measure: 'mass', label: 'Carats', symbol: 'ct', system: 'any', factor: 0.0002, autoDisplay: false, aliases: ['carat', 'carats'] });

// ----------------------------------------------------------------- length (m)

const IN_M = 0.0254;

defineMeasure({
	id: 'length',
	label: 'Length',
	canonical: 'm',
	allowNegative: false,
	systemDefaults: { metric: 'cm', imperial: 'ft-in' },
});
scaled({ id: 'mm', measure: 'length', label: 'Millimeters', system: 'metric', factor: 1e-3, aliases: ['millimeter', 'millimeters', 'millimetre', 'millimetres'] });
scaled({ id: 'cm', measure: 'length', label: 'Centimeters', system: 'metric', factor: 1e-2, aliases: ['centimeter', 'centimeters', 'centimetre', 'centimetres'] });
scaled({ id: 'm', measure: 'length', label: 'Meters', system: 'metric', factor: 1, aliases: ['meter', 'meters', 'metre', 'metres'] });
scaled({ id: 'km', measure: 'length', label: 'Kilometers', system: 'metric', factor: 1000, aliases: ['kilometer', 'kilometers', 'kilometre', 'kilometres'] });
scaled({ id: 'in', measure: 'length', label: 'Inches', system: 'imperial', factor: IN_M, aliases: ['inch', 'inches', '"'] });
scaled({ id: 'ft', measure: 'length', label: 'Feet', system: 'imperial', factor: IN_M * 12, aliases: ['foot', 'feet', "'"] });
scaled({ id: 'yd', measure: 'length', label: 'Yards', system: 'imperial', factor: IN_M * 36, aliases: ['yds', 'yard', 'yards'] });
scaled({ id: 'mi', measure: 'length', label: 'Miles', system: 'imperial', factor: IN_M * 63360, aliases: ['mile', 'miles'] });
scaled({ id: 'nmi', measure: 'length', label: 'Nautical miles', system: 'any', factor: 1852, aliases: ['nautical mile', 'nautical miles'] });
composite({ id: 'ft-in', measure: 'length', label: 'Feet and inches', system: 'imperial', parts: ['ft', 'in'], minorPerMajor: 12 });
// Sub-millimetre precision, which nothing above could express, plus the
// marine fathom. Additions to an existing family: autoDisplay:false.
scaled({ id: 'micron', measure: 'length', label: 'Microns', symbol: 'micron', system: 'metric', factor: 1e-6, autoDisplay: false, aliases: ['um', 'mcm', 'micrometer', 'micrometers', 'micrometre', 'micrometres', 'microns'] });
scaled({ id: 'mil', measure: 'length', label: 'Mils (thousandths of an inch)', symbol: 'mil', system: 'imperial', factor: 0.0000254, autoDisplay: false, aliases: ['mils', 'thou', 'thous'] });
scaled({ id: 'ftm', measure: 'length', label: 'Fathoms', symbol: 'ftm', system: 'any', factor: 1.8288, autoDisplay: false, aliases: ['fathom', 'fathoms'] });

// ------------------------------------------------------- temperature (deg C)

defineMeasure({
	id: 'temperature',
	label: 'Temperature',
	canonical: 'C',
	allowNegative: true,
	systemDefaults: { metric: 'C', imperial: 'F' },
});
custom({
	id: 'C', measure: 'temperature', label: 'Celsius', symbol: '°C', system: 'metric',
	aliases: ['degc', 'deg c', 'celsius', 'centigrade'],
	toBase: n => n,
	fromBase: b => b,
});
custom({
	id: 'F', measure: 'temperature', label: 'Fahrenheit', symbol: '°F', system: 'imperial',
	aliases: ['degf', 'deg f', 'fahrenheit'],
	toBase: n => (n - 32) * 5 / 9,
	fromBase: b => b * 9 / 5 + 32,
});
custom({
	id: 'K', measure: 'temperature', label: 'Kelvin', symbol: 'K', system: 'any',
	aliases: ['kelvin'],
	toBase: n => n - 273.15,
	fromBase: b => b + 273.15,
});

// ----------------------------------------------------------------- volume (L)
//
// ONE measure, four FAMILIES. A tank in gallons and a pile in cubic yards
// genuinely convert, so they share a measure and a canonical unit -- but a
// bulk-materials yard must never have its 7.5 yd3 read back as 1,515 gal
// because a viewer set unitSystem:'imperial', which is what `family` and
// `familyDefaults` below prevent.
//
//   fluid     the metric litre ladder and US CUSTOMARY liquid measure
//   cubic     lengths cubed, the spelling bulk materials are sold in
//   dry       US dry measure (produce, grain) -- a THIRD pint/quart
//   fluid-uk  imperial liquid measure, which is NOT the US one
//
// The US and imperial gallons differ by about 20% (3.785411784 L vs
// 4.54609 L), and so do their pints, quarts and fluid ounces. `base` is
// computed at save time and never recomputed, so changing the `gal` factor
// would silently reinterpret every gallon anyone has ever stored: the
// existing ids stay US customary and keep their exact factors, and the UK
// spellings get ids of their own. The UK units take system 'any' rather than
// 'imperial' on purpose -- autoUnit filters candidates by the default unit's
// system, so 'imperial' would let a US-gallon element auto-display UK
// gallons, a silent 20% error, and systemDefaults.imperial can only name one
// of the two anyway.
//
// The US cooking units are US CUSTOMARY, not US LEGAL: a cup is
// 236.5882365 mL (not 240) and a teaspoon 4.92892159375 mL (not 5); their
// labels say so.
//
// Every cubic factor is an EXACT decimal literal, not a derived product:
// 0.0254^3 is exact in binary but (0.0254*12)^3 * 1000 evaluates to
// 28.31684659199999, and 42 * 3.785411784 to 158.98729492799998. The
// literals are strictly better and the identities below are exact:
// 1728 in3 = 1 ft3, 27 ft3 = 1 yd3, 231 in3 = 1 gal, 43560 ft3 = 1 acre-ft,
// 144 in3 = 1 board foot, 2150.42 in3 = 1 US bushel, 160 imp fl oz =
// 1 imperial gallon (4.54609 L exactly, by the 1985 definition).

const GAL_L = 3.785411784;

defineMeasure({
	id: 'volume',
	label: 'Volume',
	canonical: 'L',
	allowNegative: false,
	systemDefaults: { metric: 'L', imperial: 'gal' },
	familyDefaults: {
		fluid: { metric: 'L', imperial: 'gal' },
		cubic: { metric: 'm3', imperial: 'yd3' },
		// US dry measure has no metric spelling of its own; litres are the
		// honest metric answer for a bushel.
		dry: { metric: 'L', imperial: 'bu' },
		'fluid-uk': { metric: 'L', imperial: 'impgal' },
	},
});
// -- fluid: the metric litre ladder. cL/dL/hL/kL are entry spellings only
//    (autoDisplay:false), so an existing mL- or L-element's `auto` display
//    cannot move: 0.25 L keeps printing "250 mL", never "2.5 dL".
scaled({ id: 'mL', measure: 'volume', label: 'Milliliters', system: 'metric', family: 'fluid', factor: 1e-3, aliases: ['milliliter', 'milliliters', 'millilitre', 'millilitres'] });
scaled({ id: 'cL', measure: 'volume', label: 'Centiliters', system: 'metric', family: 'fluid', factor: 1e-2, autoDisplay: false, aliases: ['centiliter', 'centiliters', 'centilitre', 'centilitres'] });
scaled({ id: 'dL', measure: 'volume', label: 'Deciliters', system: 'metric', family: 'fluid', factor: 1e-1, autoDisplay: false, aliases: ['deciliter', 'deciliters', 'decilitre', 'decilitres'] });
scaled({ id: 'L', measure: 'volume', label: 'Liters', system: 'metric', family: 'fluid', factor: 1, aliases: ['liter', 'liters', 'litre', 'litres'] });
scaled({ id: 'hL', measure: 'volume', label: 'Hectoliters', system: 'metric', family: 'fluid', factor: 100, autoDisplay: false, aliases: ['hectoliter', 'hectoliters', 'hectolitre', 'hectolitres'] });
scaled({ id: 'kL', measure: 'volume', label: 'Kiloliters', system: 'metric', family: 'fluid', factor: 1000, autoDisplay: false, aliases: ['kiloliter', 'kiloliters', 'kilolitre', 'kilolitres'] });
// -- fluid: US customary liquid measure.
scaled({ id: 'tsp', measure: 'volume', label: 'Teaspoons (US)', system: 'imperial', family: 'fluid', factor: GAL_L / 768, aliases: ['teaspoon', 'teaspoons'] });
scaled({ id: 'tbsp', measure: 'volume', label: 'Tablespoons (US)', system: 'imperial', family: 'fluid', factor: GAL_L / 256, aliases: ['tbs', 'tablespoon', 'tablespoons'] });
scaled({ id: 'floz', measure: 'volume', label: 'Fluid ounces (US)', symbol: 'fl oz', system: 'imperial', family: 'fluid', factor: GAL_L / 128, aliases: ['fluid ounce', 'fluid ounces'] });
scaled({ id: 'cup', measure: 'volume', label: 'Cups (US)', system: 'imperial', family: 'fluid', factor: GAL_L / 16, aliases: ['cups'] });
scaled({ id: 'pt', measure: 'volume', label: 'Pints (US)', system: 'imperial', family: 'fluid', factor: GAL_L / 8, aliases: ['pint', 'pints'] });
scaled({ id: 'qt', measure: 'volume', label: 'Quarts (US)', system: 'imperial', family: 'fluid', factor: GAL_L / 4, aliases: ['quart', 'quarts'] });
scaled({ id: 'gal', measure: 'volume', label: 'Gallons (US)', system: 'imperial', family: 'fluid', factor: GAL_L, aliases: ['gallon', 'gallons'] });
scaled({ id: 'bbl', measure: 'volume', label: 'Barrels (oil, 42 US gal)', system: 'imperial', family: 'fluid', factor: 158.987294928, autoDisplay: false, aliases: ['barrel', 'barrels', 'oil barrel', 'oil barrels'] });
// -- cubic. cm3 and mL are the same size, as are dm3/L and m3/kL: that is
//    deliberate (an engine is 350 cc, a dose is 5 mL) and the family filter
//    keeps the tied pairs apart.
scaled({ id: 'mm3', measure: 'volume', label: 'Cubic millimeters', symbol: 'mm³', system: 'metric', family: 'cubic', factor: 1e-6, aliases: ['cubic millimeter', 'cubic millimeters', 'cubic millimetre', 'cubic millimetres', 'cu mm'] });
scaled({ id: 'cm3', measure: 'volume', label: 'Cubic centimeters', symbol: 'cm³', system: 'metric', family: 'cubic', factor: 1e-3, aliases: ['cc', 'ccm', 'cubic centimeter', 'cubic centimeters', 'cubic centimetre', 'cubic centimetres', 'cu cm'] });
scaled({ id: 'dm3', measure: 'volume', label: 'Cubic decimeters', symbol: 'dm³', system: 'metric', family: 'cubic', factor: 1, aliases: ['cubic decimeter', 'cubic decimeters', 'cubic decimetre', 'cubic decimetres', 'cu dm'] });
scaled({ id: 'm3', measure: 'volume', label: 'Cubic meters', symbol: 'm³', system: 'metric', family: 'cubic', factor: 1000, aliases: ['cbm', 'cubic meter', 'cubic meters', 'cubic metre', 'cubic metres', 'cu m'] });
scaled({ id: 'in3', measure: 'volume', label: 'Cubic inches', symbol: 'in³', system: 'imperial', family: 'cubic', factor: 0.016387064, aliases: ['cubic inch', 'cubic inches', 'cu in'] });
scaled({ id: 'ft3', measure: 'volume', label: 'Cubic feet', symbol: 'ft³', system: 'imperial', family: 'cubic', factor: 28.316846592, aliases: ['cubic foot', 'cubic feet', 'cu ft'] });
scaled({ id: 'yd3', measure: 'volume', label: 'Cubic yards', symbol: 'yd³', system: 'imperial', family: 'cubic', factor: 764.554857984, aliases: ['cy', 'cubic yard', 'cubic yards', 'cu yd'] });
scaled({ id: 'acre-ft', measure: 'volume', label: 'Acre-feet', symbol: 'acre-ft', system: 'imperial', family: 'cubic', factor: 1233481.83754752, autoDisplay: false, aliases: ['acre foot', 'acre feet', 'acre-feet'] });
scaled({ id: 'fbm', measure: 'volume', label: 'Board feet', symbol: 'fbm', system: 'imperial', family: 'cubic', factor: 2.359737216, autoDisplay: false, aliases: ['board foot', 'board feet', 'boardfoot'] });
// -- US dry measure. A third pint and quart, which is exactly why they say
//    "dry" in their symbols.
scaled({ id: 'drypt', measure: 'volume', label: 'Dry pints (US)', symbol: 'dry pt', system: 'imperial', family: 'dry', factor: 0.5506104713575, aliases: ['dry pint', 'dry pints'] });
scaled({ id: 'dryqt', measure: 'volume', label: 'Dry quarts (US)', symbol: 'dry qt', system: 'imperial', family: 'dry', factor: 1.101220942715, aliases: ['dry quart', 'dry quarts'] });
scaled({ id: 'peck', measure: 'volume', label: 'Pecks (US)', symbol: 'pk', system: 'imperial', family: 'dry', factor: 8.80976754172, aliases: ['pecks'] });
scaled({ id: 'bu', measure: 'volume', label: 'Bushels (US)', symbol: 'bu', system: 'imperial', family: 'dry', factor: 35.23907016688, aliases: ['bushel', 'bushels'] });
// -- imperial (UK) liquid measure. system 'any', never 'imperial': see above.
scaled({ id: 'impfloz', measure: 'volume', label: 'Fluid ounces (imperial)', symbol: 'imp fl oz', system: 'any', family: 'fluid-uk', factor: 0.0284130625, aliases: ['imp floz', 'imperial fluid ounce', 'imperial fluid ounces', 'uk fluid ounce', 'uk fluid ounces'] });
scaled({ id: 'imppt', measure: 'volume', label: 'Pints (imperial)', symbol: 'imp pt', system: 'any', family: 'fluid-uk', factor: 0.56826125, aliases: ['imperial pint', 'imperial pints', 'uk pint', 'uk pints'] });
scaled({ id: 'impqt', measure: 'volume', label: 'Quarts (imperial)', symbol: 'imp qt', system: 'any', family: 'fluid-uk', factor: 1.1365225, aliases: ['imperial quart', 'imperial quarts', 'uk quart', 'uk quarts'] });
scaled({ id: 'impgal', measure: 'volume', label: 'Gallons (imperial)', symbol: 'imp gal', system: 'any', family: 'fluid-uk', factor: 4.54609, aliases: ['imp gallon', 'imperial gallon', 'imperial gallons', 'uk gallon', 'uk gallons'] });

// --------------------------------------------------------------- duration (s)

defineMeasure({
	id: 'duration',
	label: 'Duration',
	canonical: 's',
	allowNegative: false,
	systemDefaults: { metric: 's', imperial: 's' },
});
scaled({ id: 'ms', measure: 'duration', label: 'Milliseconds', factor: 1e-3, aliases: ['millisecond', 'milliseconds'] });
scaled({ id: 's', measure: 'duration', label: 'Seconds', factor: 1, aliases: ['sec', 'secs', 'second', 'seconds'] });
scaled({ id: 'min', measure: 'duration', label: 'Minutes', factor: 60, aliases: ['mins', 'minute', 'minutes'] });
scaled({ id: 'h', measure: 'duration', label: 'Hours', factor: 3600, aliases: ['hr', 'hrs', 'hour', 'hours'] });
scaled({ id: 'd', measure: 'duration', label: 'Days', factor: 86400, aliases: ['day', 'days'] });
scaled({ id: 'wk', measure: 'duration', label: 'Weeks', factor: 604800, aliases: ['w', 'wks', 'week', 'weeks'] });
composite({ id: 'h-min', measure: 'duration', label: 'Hours and minutes', parts: ['h', 'min'], minorPerMajor: 60 });
composite({ id: 'min-s', measure: 'duration', label: 'Minutes and seconds', parts: ['min', 's'], minorPerMajor: 60 });

// ----------------------------------------------------------------- area (m2)

const FT2_M2 = IN_M * 12 * IN_M * 12;

defineMeasure({
	id: 'area',
	label: 'Area',
	canonical: 'm2',
	allowNegative: false,
	systemDefaults: { metric: 'm2', imperial: 'ft2' },
});
scaled({ id: 'cm2', measure: 'area', label: 'Square centimeters', symbol: 'cm²', system: 'metric', factor: 1e-4, aliases: ['sq cm', 'sqcm', 'square centimeter', 'square centimeters', 'square centimetre', 'square centimetres'] });
scaled({ id: 'm2', measure: 'area', label: 'Square meters', symbol: 'm²', system: 'metric', factor: 1, aliases: ['sq m', 'sqm', 'square meter', 'square meters', 'square metre', 'square metres'] });
scaled({ id: 'ha', measure: 'area', label: 'Hectares', system: 'metric', factor: 1e4, aliases: ['hectare', 'hectares'] });
scaled({ id: 'km2', measure: 'area', label: 'Square kilometers', symbol: 'km²', system: 'metric', factor: 1e6, aliases: ['sq km', 'sqkm', 'square kilometer', 'square kilometers', 'square kilometre', 'square kilometres'] });
scaled({ id: 'ft2', measure: 'area', label: 'Square feet', symbol: 'ft²', system: 'imperial', factor: FT2_M2, aliases: ['sq ft', 'sqft', 'square foot', 'square feet'] });
scaled({ id: 'yd2', measure: 'area', label: 'Square yards', symbol: 'yd²', system: 'imperial', factor: FT2_M2 * 9, aliases: ['sq yd', 'sqyd', 'square yard', 'square yards'] });
scaled({ id: 'acre', measure: 'area', label: 'Acres', system: 'imperial', factor: 4046.8564224, aliases: ['ac', 'acres'] });
scaled({ id: 'mi2', measure: 'area', label: 'Square miles', symbol: 'mi²', system: 'imperial', factor: 4046.8564224 * 640, aliases: ['sq mi', 'sqmi', 'square mile', 'square miles'] });
// The two small squares the measure could not spell: a wound, a PCB, a tile.
scaled({ id: 'mm2', measure: 'area', label: 'Square millimeters', symbol: 'mm²', system: 'metric', factor: 1e-6, autoDisplay: false, aliases: ['sq mm', 'sqmm', 'square millimeter', 'square millimeters', 'square millimetre', 'square millimetres'] });
scaled({ id: 'in2', measure: 'area', label: 'Square inches', symbol: 'in²', system: 'imperial', factor: 0.00064516, autoDisplay: false, aliases: ['sq in', 'sqin', 'square inch', 'square inches'] });

// ---------------------------------------------------------------- speed (m/s)

defineMeasure({
	id: 'speed',
	label: 'Speed',
	canonical: 'm/s',
	allowNegative: false,
	systemDefaults: { metric: 'km/h', imperial: 'mph' },
});
scaled({ id: 'm/s', measure: 'speed', label: 'Meters per second', system: 'metric', factor: 1, aliases: ['mps', 'meters per second', 'metres per second'] });
scaled({ id: 'km/h', measure: 'speed', label: 'Kilometers per hour', system: 'metric', factor: 1000 / 3600, aliases: ['kmh', 'kph', 'kilometers per hour', 'kilometres per hour'] });
scaled({ id: 'mph', measure: 'speed', label: 'Miles per hour', system: 'imperial', factor: IN_M * 63360 / 3600, aliases: ['mi/h', 'miles per hour'] });
// 1 knot is one nautical mile per hour, 1852/3600 m/s exactly -- but this
// factor is the encoding of every knot ever stored, and 0.514444 is what
// shipped. The difference is 8.6e-7 relative: invisible on screen, and four
// orders of magnitude ABOVE the 1e-9 window every stored base is compared in
// (see the header). It stays until something rebases the stored values.
scaled({ id: 'kn', measure: 'speed', label: 'Knots', system: 'any', factor: 0.514444, aliases: ['kt', 'kts', 'knot', 'knots'] });
scaled({ id: 'ft/s', measure: 'speed', label: 'Feet per second', system: 'imperial', factor: 0.3048, autoDisplay: false, aliases: ['fps', 'ft per s', 'foot per second', 'feet per second'] });

// -------------------------------------------------------------- energy (kcal)
//
// TWO families. `autoDisplay:false` stops a unit being CHOSEN; it does not
// stop `auto` leaving the element's own default unit for a larger unit that
// IS auto-eligible, and in one flat pool the largest energy unit is the
// kilocalorie. A 12,000 BTU air conditioner would read "3,026.0 kcal" and a
// 300 Wh battery "258.1 kcal" -- arithmetically right, contextually
// unreadable, and exactly what `family` exists to prevent.
//
//   general      the joule/calorie table that shipped, unchanged: kJ is as
//                much a nutrition unit as kcal is, so they stay together
//   electrical   electrical and heating energy

defineMeasure({
	id: 'energy',
	label: 'Energy',
	canonical: 'kcal',
	allowNegative: false,
	// Both systems say kcal: energy has no metric/imperial split, so a
	// viewer's unitSystem preference has nothing to say about it and
	// familyDefaults would have nothing to add (format.js systemDefaultUnit).
	systemDefaults: { metric: 'kcal', imperial: 'kcal' },
});
scaled({ id: 'J', measure: 'energy', label: 'Joules', family: 'general', factor: 1 / 4184, aliases: ['joule', 'joules'] });
scaled({ id: 'kJ', measure: 'energy', label: 'Kilojoules', family: 'general', factor: 1 / 4.184, aliases: ['kilojoule', 'kilojoules'] });
scaled({ id: 'cal', measure: 'energy', label: 'Calories', family: 'general', factor: 1e-3, aliases: ['calorie', 'calories'] });
scaled({ id: 'kcal', measure: 'energy', label: 'Kilocalories', family: 'general', factor: 1, aliases: ['kilocalorie', 'kilocalories'] });
// Electrical and heating energy, which a kcal-only table could not spell.
// 1 kcal is 4184 J exactly (thermochemical), so these are divisions, not
// roundings. NOT added: the food 'Cal' -- aliases are lower-cased, so it
// would collide with 'cal' and brick the module at require time.
// Entry-only AND a family of their own: the family keeps `auto` out of the
// calorie table, the flag keeps a BTU element reading in BTU rather than
// climbing to the kilowatt-hour a furnace rating is not quoted in.
scaled({ id: 'MJ', measure: 'energy', label: 'Megajoules', family: 'electrical', factor: 1e6 / 4184, autoDisplay: false, aliases: ['megajoule', 'megajoules'] });
scaled({ id: 'Wh', measure: 'energy', label: 'Watt-hours', family: 'electrical', factor: 3600 / 4184, autoDisplay: false, aliases: ['watt hour', 'watt hours', 'watt-hours'] });
scaled({ id: 'kWh', measure: 'energy', label: 'Kilowatt-hours', family: 'electrical', factor: 3600000 / 4184, autoDisplay: false, aliases: ['kilowatt hour', 'kilowatt hours', 'kilowatt-hours'] });
scaled({ id: 'BTU', measure: 'energy', label: 'British thermal units', family: 'electrical', factor: 1055.05585262 / 4184, autoDisplay: false, aliases: ['btus', 'british thermal unit', 'british thermal units'] });

// ------------------------------------------------------------- pressure (kPa)

// 1 mmHg is 13595.1 kg/m3 x 9.80665 m/s2 x 1 mm = 133.322387415 Pa exactly,
// and 1 psi is 4.4482216152605 N / 0.00064516 m2 = 6.894757293168361 kPa.
// Both are carried here as the six-figure roundings they shipped as, and
// inHg is derived from the mmHg constant so it keeps its rounding too.
// Correcting them would move a stored 140 mmHg base by 5.4e-5 kPa, a stored
// 32 psi base by 9.4e-6 and a stored 29.92 inHg base by 2.9e-4 -- four to
// five orders of magnitude outside the 1e-9 window the compiler and the rule
// matcher compare bases in, so "notify when systolic >= 140 mmHg" would stop
// firing for every reading taken before the deploy. See the header: a factor
// is a storage format.
const MMHG_KPA = 0.133322;

defineMeasure({
	id: 'pressure',
	label: 'Pressure',
	canonical: 'kPa',
	allowNegative: false,
	systemDefaults: { metric: 'kPa', imperial: 'psi' },
	familyDefaults: {
		general: { metric: 'kPa', imperial: 'psi' },
		// A barometer has no imperial spelling INSIDE its family (inHg keeps
		// the general family it shipped in), so an imperial viewer reads a
		// station pressure in the hectopascals it was recorded in.
		weather: { metric: 'hPa', imperial: 'hPa' },
	},
});
scaled({ id: 'Pa', measure: 'pressure', label: 'Pascals', system: 'metric', family: 'general', factor: 1e-3, aliases: ['pascal', 'pascals'] });
scaled({ id: 'kPa', measure: 'pressure', label: 'Kilopascals', system: 'metric', family: 'general', factor: 1, aliases: ['kilopascal', 'kilopascals'] });
scaled({ id: 'bar', measure: 'pressure', label: 'Bar', system: 'metric', family: 'general', factor: 100, aliases: ['bars'] });
scaled({ id: 'psi', measure: 'pressure', label: 'Pounds per square inch', system: 'imperial', family: 'general', factor: 6.894757, aliases: ['lbf/in2', 'pounds per square inch'] });
scaled({ id: 'mmHg', measure: 'pressure', label: 'Millimeters of mercury', system: 'any', family: 'general', factor: MMHG_KPA, aliases: ['torr'] });
scaled({ id: 'inHg', measure: 'pressure', label: 'Inches of mercury', system: 'imperial', family: 'general', factor: MMHG_KPA * 25.4, aliases: ['inches of mercury'] });
// Industry, and the one pressure everybody quotes.
scaled({ id: 'MPa', measure: 'pressure', label: 'Megapascals', system: 'metric', family: 'general', factor: 1000, autoDisplay: false, aliases: ['megapascal', 'megapascals'] });
scaled({ id: 'atm', measure: 'pressure', label: 'Atmospheres', system: 'any', family: 'general', factor: 101.325, autoDisplay: false, aliases: ['atmosphere', 'atmospheres', 'standard atmosphere'] });
// The barometer, in a family of its own. Weather is quoted to four figures
// in hectopascals and the unit above it is the bar: in one flat family a
// 1,013 hPa reading and a 1,014 hPa one both read "1.0 bar", which is the
// whole barometric range collapsed into one printed digit.
scaled({ id: 'hPa', measure: 'pressure', label: 'Hectopascals', system: 'metric', family: 'weather', factor: 0.1, autoDisplay: false, aliases: ['hectopascal', 'hectopascals', 'mbar', 'millibar', 'millibars'] });

// -------------------------------------------------------------- data-size (B)
//
// THREE families, for the same reason volume has four: they all convert, and
// reading one as another is the error.
//
//   decimal   the SI byte ladder, unchanged
//   binary    what an operating system actually reports, and what the
//             decimal ladder is routinely mistaken for: 1 GiB is
//             1,073,741,824 bytes, 7.4% more than 1 GB
//   bits      a bit is an eighth of a byte; "1,500 bit" is a line rate, and
//             "187.5 B" is not the thing anyone was looking at

defineMeasure({
	id: 'data-size',
	label: 'Data size',
	canonical: 'B',
	allowNegative: false,
	// Both systems say B: data size has no metric/imperial split, so a
	// unitSystem preference resolves to the element's own unit and
	// familyDefaults has nothing to add (format.js systemDefaultUnit).
	systemDefaults: { metric: 'B', imperial: 'B' },
});
scaled({ id: 'B', measure: 'data-size', label: 'Bytes', family: 'decimal', factor: 1, aliases: ['byte', 'bytes'] });
scaled({ id: 'KB', measure: 'data-size', label: 'Kilobytes', family: 'decimal', factor: 1e3, aliases: ['kilobyte', 'kilobytes'] });
scaled({ id: 'MB', measure: 'data-size', label: 'Megabytes', family: 'decimal', factor: 1e6, aliases: ['megabyte', 'megabytes'] });
scaled({ id: 'GB', measure: 'data-size', label: 'Gigabytes', family: 'decimal', factor: 1e9, aliases: ['gigabyte', 'gigabytes'] });
scaled({ id: 'TB', measure: 'data-size', label: 'Terabytes', family: 'decimal', factor: 1e12, aliases: ['terabyte', 'terabytes'] });
scaled({ id: 'PB', measure: 'data-size', label: 'Petabytes', family: 'decimal', factor: 1e15, autoDisplay: false, aliases: ['petabyte', 'petabytes'] });
// The id 'bit' rather than the symbol 'b', which would collide with 'B' for
// byte once aliases are lower-cased.
scaled({ id: 'bit', measure: 'data-size', label: 'Bits', symbol: 'bit', family: 'bits', factor: 0.125, autoDisplay: false, aliases: ['bits'] });
scaled({ id: 'KiB', measure: 'data-size', label: 'Kibibytes', family: 'binary', factor: 1024, autoDisplay: false, aliases: ['kibibyte', 'kibibytes'] });
scaled({ id: 'MiB', measure: 'data-size', label: 'Mebibytes', family: 'binary', factor: 1048576, autoDisplay: false, aliases: ['mebibyte', 'mebibytes'] });
scaled({ id: 'GiB', measure: 'data-size', label: 'Gibibytes', family: 'binary', factor: 1073741824, autoDisplay: false, aliases: ['gibibyte', 'gibibytes'] });
scaled({ id: 'TiB', measure: 'data-size', label: 'Tebibytes', family: 'binary', factor: 1099511627776, autoDisplay: false, aliases: ['tebibyte', 'tebibytes'] });

// ------------------------------------------------------------------- count (each)
//
// Things you count rather than measure. Every unit here is NON-CONVERTIBLE:
// they share the measure so one picker can offer them, and convert() refuses
// to move a value between any two, because "3 tablets in puffs" is not a
// rounding question but a category error.
//
// Where they come from: dosing (tablets, capsules, drops, puffs, sprays,
// insulin units, international units, milliequivalents), vital signs (beats,
// breaths -- counted per minute, as a rate below), inventory (each, case,
// pallet, dozen), and anything else whose magnitude is a tally.

defineMeasure({
	id: 'count',
	label: 'Count',
	canonical: 'each',
	allowNegative: false,
	systemDefaults: { metric: 'each', imperial: 'each' },
});
const COUNT_UNITS = [
	['each', 'Each', ['ea', 'item', 'items']],
	['tablet', 'Tablets', ['tab', 'tabs', 'tablets']],
	['capsule', 'Capsules', ['cap', 'caps', 'capsules']],
	['dose', 'Doses', ['doses']],
	['drop', 'Drops', ['gtt', 'drops']],
	['puff', 'Puffs', ['puffs']],
	['spray', 'Sprays', ['sprays']],
	['patch', 'Patches', ['patches']],
	['vial', 'Vials', ['vials']],
	['IU', 'International units', ['iu', 'international unit', 'international units']],
	['unit', 'Units', ['units']],
	['mEq', 'Milliequivalents', ['meq', 'milliequivalent', 'milliequivalents']],
	['beat', 'Beats', ['beats']],
	['breath', 'Breaths', ['breaths']],
	['dozen', 'Dozens', ['dz', 'dozens']],
	['case', 'Cases', ['cases']],
	['pallet', 'Pallets', ['pallets']],
	['pair', 'Pairs', ['pairs']],
	['pack', 'Packs', ['packs']],
	['box', 'Boxes', ['boxes']],
	['bag', 'Bags', ['bags']],
	['roll', 'Rolls', ['rolls']],
	['sheet', 'Sheets', ['sheets']],
	['bundle', 'Bundles', ['bundles']],
	['load', 'Loads', ['loads']],
	// The common things counted in a kitchen, a barn and a craft room
	// (KPR-1109, the owner's ruling 2026-10-22: the built-in count units grow
	// with the common ones, and an element's own units take the long tail).
	// Each is a word with a plural, printed as one ("2.0 pinches", "1 pinch":
	// the fourth entry). They come BEFORE the rates below, so each has its
	// five count rates (bale/d, pinch/min) like every count unit before it.
	['pinch', 'Pinches', [], 'pinches'],
	['dash', 'Dashes', [], 'dashes'],
	['handful', 'Handfuls', [], 'handfuls'],
	['clove', 'Cloves', [], 'cloves'],
	['slice', 'Slices', [], 'slices'],
	['can', 'Cans', [], 'cans'],
	['jar', 'Jars', [], 'jars'],
	['stick', 'Sticks', [], 'sticks'],
	['bunch', 'Bunches', [], 'bunches'],
	['sprig', 'Sprigs', [], 'sprigs'],
	['head', 'Heads', [], 'heads'],
	['bale', 'Bales', [], 'bales'],
	['flake', 'Flakes', [], 'flakes'],
	['crate', 'Crates', [], 'crates'],
	['carton', 'Cartons', [], 'cartons'],
	['sack', 'Sacks', [], 'sacks'],
	['tray', 'Trays', [], 'trays'],
	['skein', 'Skeins', [], 'skeins'],
	// The owner's "pinch and piece" (KPR-1085's units question, 2026-10-21).
	['piece', 'Pieces', ['pc', 'pcs'], 'pieces'],
];
for (const entry of COUNT_UNITS) {
	scaled({
		id: entry[0], measure: 'count', label: entry[1], symbol: entry[0],
		factor: 1, convertible: false, aliases: entry[2], plural: entry[3],
	});
}

// ------------------------------------------------------------------ rates
//
// A RATE is an amount of one measure per unit of time: a feeding pump's
// 100 mL/h, a drip's 20 drops/min, a dose's 5 mcg/min, a baby's 30 g/d. Each
// rate is a MEASURE of its own, generated from the measure it counts, because
// that is the one shape nothing downstream has to learn: KQL, rules,
// thresholds, sort, charts and exports all compare ONE `base` number per
// measure, and 100 mL and 100 mL/h must never be the same number in the same
// comparison -- which a per-time option on the volume measure would have
// made them.
//
// The units are the cross product of the counted measure's plain units (never
// a composite: "lb-oz per week" is nobody's spelling) and RATE_TIMES. A month
// and a year are not fixed lengths of time, so they are not times here. Unit
// 'mL/h' is symbol 'mL/h', label "Milliliters per hour", and carries the
// `numerator` ('mL') and `per` ('h') it is made of.
//
//   family       the numerator's family and the time: 'fluid/h'. `auto` and
//                the unitSystem preference stay inside a family, so a pump in
//                mL/h may read as L/h but never as mL/min -- the time a rate
//                was set up in is part of what it says.
//   system,      the numerator's. A rate of an entry-only unit (cL, bbl, gr)
//   autoDisplay  is entry-only too.
//   convertible  the numerator's. A rate of a COUNT refuses conversion as the
//                count does, with the one exception convert.js makes: the
//                same thing counted converts across times (60 drops/min is
//                3,600 drops/h), never to another thing (drops to tablets).
//
// THE CANONICAL UNIT IS CHOSEN FOR THE WINDOW. Every stored base is compared
// inside an absolute 1e-9 window (see the header), and a rate's small
// spellings are tiny in SI terms -- 1 mcg/min is 1.7e-11 kg/s, far inside the
// window, so every microgram rate would equal every other. The canonical
// units below keep the smallest spelling anyone writes well above it:
// 1 mm3/wk is 6.0e-6 mL/h, 1 mcg/wk 6.0e-6 mg/h, 1 each/wk 6.0e-3 each/h.
// `scale` is the exact literal from the counted measure's canonical unit to
// the rate's numerator (L to mL is 1000), and every factor is computed ONCE,
// in ONE order -- ((factor x scale) x 3600) / seconds -- because, like every
// factor in this file, it is a storage format: test/units.test.js pins them.

const RATE_TIMES = [
	{ id: 's', word: 'second' },
	{ id: 'min', word: 'minute' },
	{ id: 'h', word: 'hour' },
	{ id: 'd', word: 'day' },
	{ id: 'wk', word: 'week' },
];
// Every rate's canonical unit is per hour.
const RATE_CANONICAL_SECONDS = 3600;

function isRateTime(unitId) {
	return RATE_TIMES.some(time => time.id === unitId);
}

function rate(def) {
	const factor = def.factor;
	const unit = {
		id: def.id,
		measure: def.measure,
		label: def.label,
		symbol: def.symbol,
		system: def.system,
		family: def.family,
		aliases: null,
		convertible: def.convertible,
		autoDisplay: def.autoDisplay,
		decreasing: false,
		joined: false,
		numerator: def.numerator,
		per: def.per,
		toBase: n => n * factor,
		fromBase: b => b / factor,
	};
	unit.aliases = registerAliases(unit, [unit.id, unit.symbol].concat(def.aliases || []));
	return addUnit(unit);
}

function defineRate(def) {
	const of = MEASURES[def.of];
	const numerators = of.units.map(id => UNITS[id]).filter(unit => !unit.parts);
	const canonical = def.canonical.split('/');
	const canonicalNumerator = UNITS[canonical[0]];
	if (!canonicalNumerator || canonicalNumerator.measure !== of.id || canonical[1] !== 'h'
		|| canonicalNumerator.toBase(1) * def.scale !== 1) {
		throw new Error('rate "' + def.id + '" names a canonical unit its scale does not reach');
	}
	const perTime = (pair, timeId) => ({ metric: pair.metric + '/' + timeId, imperial: pair.imperial + '/' + timeId });
	// The unitSystem preference answers what the counted measure answers for
	// the numerator's family, per the same time. A measure with no metric /
	// imperial split (a count) has nothing to say about its rate either.
	let familyDefaults = null;
	if (of.systemDefaults.metric !== of.systemDefaults.imperial) {
		familyDefaults = {};
		for (const time of RATE_TIMES) {
			for (const num of numerators) {
				const family = num.family + '/' + time.id;
				if (familyDefaults[family]) continue;
				familyDefaults[family] = perTime((of.familyDefaults && of.familyDefaults[num.family]) || of.systemDefaults, time.id);
			}
		}
	}
	defineMeasure({
		id: def.id,
		label: def.label,
		canonical: def.canonical,
		allowNegative: of.allowNegative,
		systemDefaults: perTime(of.systemDefaults, 'h'),
		familyDefaults,
		rateOf: of.id,
	});
	const labels = def.labels || {};
	const symbols = def.symbols || {};
	const aliases = def.aliases || {};
	for (const time of RATE_TIMES) {
		const timeUnit = UNITS[time.id];
		const seconds = timeUnit.toBase(1);
		for (const num of numerators) {
			const id = num.id + '/' + time.id;
			rate({
				id,
				measure: def.id,
				label: (labels[num.id] || num.label) + ' per ' + time.word,
				symbol: symbols[id] || num.symbol + '/' + timeUnit.symbol,
				system: num.system,
				family: num.family + '/' + time.id,
				convertible: num.convertible,
				autoDisplay: num.autoDisplay,
				numerator: num.id,
				per: time.id,
				factor: num.toBase(1) * def.scale * RATE_CANONICAL_SECONDS / seconds,
				aliases: aliases[id],
			});
		}
	}
}

defineRate({
	id: 'volume-rate',
	label: 'Volume per time',
	of: 'volume',
	canonical: 'mL/h',
	scale: 1000,
	aliases: { 'L/min': ['lpm'], 'gal/min': ['gpm'], 'gal/h': ['gph'], 'ft3/min': ['cfm'] },
});
defineRate({
	id: 'mass-rate',
	label: 'Mass per time',
	of: 'mass',
	canonical: 'mg/h',
	scale: 1e6,
});
defineRate({
	id: 'count-rate',
	label: 'Count per time',
	of: 'count',
	canonical: 'each/h',
	scale: 1,
	// "Each per hour" is not English; the count's own aliases say item.
	labels: { each: 'Items' },
	// A pulse is written "72 bpm" everywhere a pulse is written.
	symbols: { 'beat/min': 'bpm' },
});

// The three measures below came after the rates (data types second pass, PR 8)
// and are defined after them so no existing measure moves in MEASURE_LIST.

// ----------------------------------------------------------------- angle (deg)
//
// A plane angle: a heading, a roof's pitch, a joint's range of motion, a
// scope's adjustment. The canonical unit is the DEGREE, which is what people
// write; the window is no concern (1 arcsec is 2.8e-4 deg, 277,778 windows).
// Angles are never wrapped -- 370 degrees is a rotation of 370 degrees, and a
// heading that must stay inside 0..360 says so with min/max -- and they may be
// negative (a -5 degree tilt).
//
// FOUR families, because the four spellings are four traditions that convert
// but never stand in for one another: `auto` on a degree element may step down
// the sexagesimal ladder to arcminutes and arcseconds, and must never climb to
// radians, which every angle over 57.3 degrees would, or to turns.
// Not aliased: the prime and double prime. People type them as ' and ", which
// are feet and inches.

defineMeasure({
	id: 'angle',
	label: 'Angle',
	canonical: 'deg',
	allowNegative: true,
	systemDefaults: { metric: 'deg', imperial: 'deg' },
});
scaled({ id: 'deg', measure: 'angle', label: 'Degrees', symbol: '°', family: 'degrees', factor: 1, joined: true, aliases: ['degs', 'degree', 'degrees'] });
scaled({ id: 'arcmin', measure: 'angle', label: 'Arcminutes', symbol: 'arcmin', family: 'degrees', factor: 1 / 60, aliases: ['moa', 'arcminute', 'arcminutes', 'arc minute', 'arc minutes', 'minute of angle', 'minutes of angle'] });
scaled({ id: 'arcsec', measure: 'angle', label: 'Arcseconds', symbol: 'arcsec', family: 'degrees', factor: 1 / 3600, aliases: ['arcsecond', 'arcseconds', 'arc second', 'arc seconds'] });
scaled({ id: 'rad', measure: 'angle', label: 'Radians', symbol: 'rad', family: 'radians', factor: 180 / Math.PI, aliases: ['rads', 'radian', 'radians'] });
scaled({ id: 'grad', measure: 'angle', label: 'Gradians', symbol: 'grad', family: 'gradians', factor: 0.9, aliases: ['grads', 'gon', 'gons', 'gradian', 'gradians'] });
scaled({ id: 'turn', measure: 'angle', label: 'Turns', symbol: 'turn', family: 'turns', factor: 360, aliases: ['turns', 'rev', 'revs', 'revolution', 'revolutions', 'rotation', 'rotations'] });

// ------------------------------------------------------------- frequency (Hz)
//
// Cycles per second. TWO families: the hertz ladder, which `auto` climbs
// (2,400,000,000 Hz reads 2.4 GHz), and rpm, which it must never leave -- an
// engine at 3,000 rpm is not "50.0 Hz" to the person who logged it.
//
// rpm is a FREQUENCY, not a count rate. A count per time (`count-rate`:
// drop/min, beat/min printed bpm) counts things and converts only across
// times for the same thing; a frequency is how often one thing repeats. 60 bpm
// and 60 rpm are the same number per second, and they still never convert
// into each other: they are two measures, as drops and tablets are two things.

defineMeasure({
	id: 'frequency',
	label: 'Frequency',
	canonical: 'Hz',
	allowNegative: false,
	systemDefaults: { metric: 'Hz', imperial: 'Hz' },
});
scaled({ id: 'Hz', measure: 'frequency', label: 'Hertz', family: 'hertz', factor: 1, aliases: ['hertz', 'cycles per second'] });
scaled({ id: 'kHz', measure: 'frequency', label: 'Kilohertz', family: 'hertz', factor: 1e3, aliases: ['kilohertz'] });
scaled({ id: 'MHz', measure: 'frequency', label: 'Megahertz', family: 'hertz', factor: 1e6, aliases: ['megahertz'] });
scaled({ id: 'GHz', measure: 'frequency', label: 'Gigahertz', family: 'hertz', factor: 1e9, aliases: ['gigahertz'] });
scaled({ id: 'rpm', measure: 'frequency', label: 'Revolutions per minute', family: 'rpm', factor: 1 / 60, aliases: ['r/min', 'rev/min', 'revs/min', 'rev per min', 'revs per minute', 'revolutions per minute', 'rotations per minute'] });

// ------------------------------------------------------- fuel economy (km/L)
//
// Distance per fuel, or fuel per distance: the one measure whose spellings
// run in OPPOSITE directions. The canonical unit is km/L, an EFFICIENCY, so
// three of the four units are plain multiples and a bigger base is always the
// better economy; L/100km is a CONSUMPTION, the reciprocal() of it, whose
// number falls as the base rises (see the header: `decreasing`). Zero is
// refused (allowZero:false) -- 0 L/100km has no base and 0 mpg has no
// consumption.
//
// FOUR families, one unit each: a US and a UK mile-per-gallon differ by 20%
// (their gallons do), and km/L and L/100km are regional conventions rather
// than a ladder. `auto` has nothing to choose between (format.js), and the
// unitSystem preference maps each family to the spelling a metric or imperial
// reader expects: litres per 100 km for metric, US mpg for imperial -- a UK
// mpg element stays UK, as an imperial gallon does.
// mpg is the exact decimal 1.609344 km per mile over the US gallon, computed
// once in that order; like every factor here it is a storage format.

const KM_PER_MILE = 1.609344;

defineMeasure({
	id: 'fuel-economy',
	label: 'Fuel economy',
	canonical: 'km/L',
	allowNegative: false,
	allowZero: false,
	systemDefaults: { metric: 'L/100km', imperial: 'mpg' },
	familyDefaults: {
		'mpg-us': { metric: 'L/100km', imperial: 'mpg' },
		'mpg-uk': { metric: 'L/100km', imperial: 'mpg-uk' },
		'km-per-litre': { metric: 'km/L', imperial: 'mpg' },
		'litres-per-100km': { metric: 'L/100km', imperial: 'mpg' },
	},
});
scaled({ id: 'mpg', measure: 'fuel-economy', label: 'Miles per gallon (US)', system: 'imperial', family: 'mpg-us', factor: KM_PER_MILE / GAL_L, aliases: ['mpg-us', 'us mpg', 'mi/gal', 'miles per gallon'] });
scaled({ id: 'mpg-uk', measure: 'fuel-economy', label: 'Miles per gallon (imperial)', symbol: 'imp mpg', system: 'any', family: 'mpg-uk', factor: KM_PER_MILE / 4.54609, aliases: ['uk mpg', 'mpg uk', 'imperial mpg', 'miles per imperial gallon', 'miles per uk gallon'] });
scaled({ id: 'km/L', measure: 'fuel-economy', label: 'Kilometers per liter', system: 'metric', family: 'km-per-litre', factor: 1, aliases: ['kmpl', 'km per l', 'km per liter', 'km per litre', 'kilometers per liter', 'kilometres per litre'] });
reciprocal({ id: 'L/100km', measure: 'fuel-economy', label: 'Liters per 100 kilometers', system: 'metric', family: 'litres-per-100km', factor: 100, aliases: ['l/100 km', 'l per 100km', 'l per 100 km', 'liters per 100 km', 'litres per 100 km', 'liters per 100 kilometers', 'litres per 100 kilometres'] });

// ------------------------------------------------------------- torque (N-m)
//
// A twisting force: a lug nut's tightening spec, a bolt, a bicycle stem, a
// wrench's range. Added by KPR-1210 (Vehicles needed a lug torque column), so
// it is defined last and no earlier measure moves in MEASURE_LIST.
//
// The canonical unit is the newton-metre. Torque is dimensionally an energy
// and is deliberately NOT one: a joule is an amount of work and a tightening
// spec is not, so `torque` is a measure of its own, N-m never converts to J,
// and no alias is shared between the two.
//
// THREE FAMILIES, one unit each (as fuel economy has): a foot-pound and an
// inch-pound do convert (12 in-lb is 1 ft-lb), but nothing should re-express
// a 100 in-lb screw spec as "8.3 ft-lb" behind the reader's back, nor a 90
// ft-lb lug nut as 1,080 in-lb, so `auto` has nothing to choose between and
// the unitSystem preference maps each family to its own spelling: a metric
// reader sees a ft-lb or in-lb element in N-m, an imperial reader keeps the
// unit it was written in.
//
// Ids are ASCII; the symbols are written the way a wrench is marked, with a
// middle dot. The parser reads a unit up to the next digit, a decimal point or
// a fraction character, so NO alias holds a full stop. 'nm' is the newton-metre
// here: nothing else in the registry is a nanometre.
// The pound-force is the pound-mass times standard gravity, 0.45359237 kg
// by 9.80665 m/s2, and the foot and the inch are exact; like every factor in
// this file these are a storage format, computed once in this order.

const LBF_N = LB_KG * 9.80665;
const FT_LBF_NM = LBF_N * IN_M * 12;
const IN_LBF_NM = LBF_N * IN_M;

defineMeasure({
	id: 'torque',
	label: 'Torque',
	canonical: 'N-m',
	allowNegative: false,
	systemDefaults: { metric: 'N-m', imperial: 'ft-lb' },
	familyDefaults: {
		'newton-metres': { metric: 'N-m', imperial: 'ft-lb' },
		'foot-pounds': { metric: 'N-m', imperial: 'ft-lb' },
		'inch-pounds': { metric: 'N-m', imperial: 'in-lb' },
	},
});
// Every spelling of a dot: the middle dot people type, the dot operator and
// the bullet operator word processors substitute. Hyphen, space and none read
// too, and so do the pounds-force and the word order a torque chart prints
// ("lb-ft").
function torqueSpellings(parts) {
	const joiners = ['-', '\u00b7', '\u22c5', '\u2219', ' ', ''];
	const out = [];
	for (const [a, b] of parts) for (const joiner of joiners) out.push(a + joiner + b);
	return out;
}
scaled({
	id: 'N-m', measure: 'torque', label: 'Newton-meters', symbol: 'N\u00b7m', system: 'metric', family: 'newton-metres', factor: 1,
	aliases: torqueSpellings([['n', 'm']]).concat(['newton-meter', 'newton-meters', 'newton meter', 'newton meters', 'newton-metre', 'newton-metres', 'newton metre', 'newton metres']),
});
scaled({
	id: 'ft-lb', measure: 'torque', label: 'Foot-pounds', symbol: 'ft\u00b7lb', system: 'imperial', family: 'foot-pounds', factor: FT_LBF_NM,
	aliases: torqueSpellings([['ft', 'lb'], ['ft', 'lbs'], ['ft', 'lbf'], ['lb', 'ft'], ['lbf', 'ft']])
		.concat(['foot-pound', 'foot-pounds', 'foot pound', 'foot pounds', 'pound-foot', 'pound-feet', 'pound foot', 'pound feet']),
});
scaled({
	id: 'in-lb', measure: 'torque', label: 'Inch-pounds', symbol: 'in\u00b7lb', system: 'imperial', family: 'inch-pounds', factor: IN_LBF_NM,
	aliases: torqueSpellings([['in', 'lb'], ['in', 'lbs'], ['in', 'lbf'], ['lb', 'in'], ['lbf', 'in']])
		.concat(['inch-pound', 'inch-pounds', 'inch pound', 'inch pounds', 'pound-inch', 'pound-inches', 'pound inch', 'pound inches']),
});

// ------------------------------------------------------------------ freeze

// The registry is shared, read-only data: a caller that pushes into
// `MEASURES.mass.units` or reassigns a factor would corrupt every later
// conversion, so everything reachable from the exports is frozen.
function deepFreeze(value) {
	if (value && typeof value === 'object' && !Object.isFrozen(value)) {
		Object.freeze(value);
		for (const key of Object.keys(value)) deepFreeze(value[key]);
	}
	return value;
}
deepFreeze(MEASURES);
deepFreeze(MEASURE_LIST);
deepFreeze(UNITS);
deepFreeze(ALIASES);
deepFreeze(RATE_TIMES);

// ------------------------------------------------------------------ lookups

function getMeasure(id) {
	if (typeof id !== 'string') return null;
	return Object.prototype.hasOwnProperty.call(MEASURES, id) ? MEASURES[id] : null;
}

function getUnit(id) {
	if (typeof id !== 'string') return null;
	return Object.prototype.hasOwnProperty.call(UNITS, id) ? UNITS[id] : null;
}

/** The unit objects of a measure, in registry order; [] for an unknown measure. */
function unitsOf(measureId) {
	const measure = getMeasure(measureId);
	return measure ? measure.units.map(id => UNITS[id]) : [];
}

function isUnitOf(unitId, measureId) {
	const unit = getUnit(unitId);
	return !!unit && unit.measure === measureId;
}

function isComposite(unitId) {
	const unit = getUnit(unitId);
	return !!unit && Array.isArray(unit.parts);
}

/** An id, or an alias in any case. No measure filter, no rate spelling. */
function lookupSpelling(trimmed) {
	const unit = getUnit(trimmed);
	if (unit) return unit;
	const key = trimmed.toLowerCase().replace(/\s+/g, ' ');
	const id = Object.prototype.hasOwnProperty.call(ALIASES, key) ? ALIASES[key] : null;
	return id ? UNITS[id] : null;
}

// "mL/hr", "ml per hour", "cups a day": a rate spelled as its two parts, when
// the table has no spelling for the whole. Each part resolves on its own --
// the first to any unit, the second to one of RATE_TIMES -- and together they
// must name a unit that exists: a rate, or a speed ("km per hr" is km/h). One
// separator only: "mcg/kg/min" is a dose per body weight, which nothing here
// measures, and must not quietly read as something else.
//
// LINEAR, AND BOUNDED, ON PURPOSE. Every spelling no alias matches comes
// through here -- for every measurement element, plain ones too -- and that
// includes whatever an anonymous visitor types into a public intake form,
// which the row validator reads BEFORE the bot check. A backtracking split
// (/^(.+?)\s*\/\s*(.+)$/) is quadratic over a run of spaces: 100 KB of them
// held the event loop for seconds. So the parts are found with indexOf, and
// nothing longer than the longest real spelling ("imperial fluid ounces per
// minute" is 32 characters) is looked at all.
const MAX_RATE_SPELLING = 64;
const RATE_JOINERS = [' per ', ' an ', ' a '];

function ratePair(numeratorText, perText) {
	const numerator = lookupSpelling(numeratorText.trim());
	const per = lookupSpelling(perText.trim());
	if (!numerator || !per || !isRateTime(per.id)) return null;
	return lookupSpelling(numerator.id + '/' + per.id);
}

function lookupRateSpelling(trimmed) {
	if (trimmed.length > MAX_RATE_SPELLING) return null;
	const slash = trimmed.indexOf('/');
	if (slash > 0) {
		const unit = ratePair(trimmed.slice(0, slash), trimmed.slice(slash + 1));
		if (unit) return unit;
	}
	// The earliest joining word, as a whole word: "tablets a day".
	const spaced = ' ' + trimmed.toLowerCase().replace(/\s+/g, ' ') + ' ';
	let at = -1;
	let joiner = null;
	for (const word of RATE_JOINERS) {
		const found = spaced.indexOf(word);
		if (found > 0 && (at === -1 || found < at)) {
			at = found;
			joiner = word;
		}
	}
	return joiner ? ratePair(spaced.slice(0, at), spaced.slice(at + joiner.length)) : null;
}

/**
 * Resolve a spelling (an id, a symbol, or an alias, any case, or a rate
 * spelled as its parts) to a unit. Restricted to `measureId` when given. Null
 * when nothing matches.
 */
function resolveUnit(spelling, measureId) {
	if (typeof spelling !== 'string') return null;
	const trimmed = spelling.trim();
	if (!trimmed) return null;
	const unit = lookupSpelling(trimmed) || lookupRateSpelling(trimmed);
	if (!unit) return null;
	if (measureId !== undefined && measureId !== null && unit.measure !== measureId) return null;
	return unit;
}

/** The stored shape: a plain object with a finite `value` and a string `unit`. */
function isMeasurementValue(v) {
	return !!v
		&& typeof v === 'object'
		&& !Array.isArray(v)
		&& typeof v.value === 'number'
		&& Number.isFinite(v.value)
		&& typeof v.unit === 'string';
}

// ------------------------------------------------------------------ several measures
//
// An element may allow SEVERAL measures (KPR-1085, `options.measures`): a
// recipe's amount in volume, mass or count -- 2 cups of milk, 250 g of flour,
// 3 eggs. Its `measure` stays the PRIMARY one: the measure of `defaultUnit`,
// of a bare number, and of what a reader that only knows `options.measure`
// sees. A stored value is still { value, unit, base }. A unit id, and every
// alias, names exactly ONE measure in this registry (registerAliases refuses
// a second), so the unit says which measure a value is in, and `base` is in
// THAT measure's canonical unit. Two values are compared on base only within
// one measure: 250 g (base 0.25 kg) is never "more" than 1 cup (0.24 L).

/**
 * The most measures one element may allow, its primary among them. A value
 * of an element that allows several is read by trying each in turn, so the
 * cost of a read grows with the list; a recipe's amount needs three. The
 * card definition gate refuses a longer list (400 invalid_options), and the
 * card editor offers no more.
 */
const MAX_MEASURES = 5;

/**
 * allowedMeasures(options) -> [measureId, ...]
 * The measures an element allows, the primary first. [measure] for a
 * single-measure element; [] for an unknown measure. `options.measures`
 * counts only as a list that names the primary measure; an id in it that is
 * unknown, or repeated, is skipped. A read rule, so tolerant: the card
 * definition gate (keepr-api utils/measurementUtils.js) refuses a malformed
 * list before it is ever stored.
 */
function allowedMeasures(options) {
	const opts = options || {};
	const primary = getMeasure(opts.measure);
	// The single-measure options one OWN unit's value is read by (scopeOptions
	// below): that unit is the whole of what they allow.
	if (!primary) return isOwnUnitId(opts.measure) && ownUnitOf(opts.measure, opts) ? [opts.measure] : [];
	const out = [primary.id];
	const list = Array.isArray(opts.measures) ? opts.measures : null;
	if (list && list.indexOf(primary.id) !== -1) {
		for (const id of list) {
			const measure = getMeasure(id);
			if (measure && out.indexOf(measure.id) === -1) out.push(measure.id);
		}
	}
	// The element's own units (KPR-1109), after its registry measures: each is
	// a measure of one unit, so a value in one is compared, totalled and sorted
	// with values in that unit only.
	for (const own of ownUnitsOf(opts)) if (out.indexOf(own.id) === -1) out.push(own.id);
	return out;
}

/** True when an element's options allow more than one measure (own units count, KPR-1109). */
function isMultiMeasure(options) {
	return allowedMeasures(options).length > 1;
}

/**
 * The measure a unit id belongs to, or null for an unknown unit. An OWN unit
 * (KPR-1109) is a measure of its own: read off the id's prefix, so every pin
 * and group built per measure is per own unit with no element options at hand.
 */
function measureOfUnit(unitId) {
	const unit = getUnit(unitId);
	if (unit) return unit.measure;
	return isOwnUnitId(unitId) ? unitId : null;
}

/** The unit ids of a measure, in registry order; [] for an unknown measure; an own unit's is itself. */
function unitIdsOf(measureId) {
	const measure = getMeasure(measureId);
	if (measure) return measure.units.slice();
	return isOwnUnitId(measureId) ? [measureId] : [];
}

/**
 * scopeOptions(options, measureId, unitId) -> options | null
 *
 * The SINGLE-measure options one value in `measureId` is read, written and
 * printed by, so every single-measure rule applies to it unchanged:
 *   - a single-measure element: `options` itself (the same object);
 *   - the primary measure of a multi-measure element: the options less
 *     `measures`, the allowlist cut to that measure's units;
 *   - another measure the element allows: that measure, with `unitId` (the
 *     unit the value is in; else the measure's canonical unit) as its default
 *     unit, the allowlist cut to its units, the display's precision with its
 *     'entered' or 'auto' (a fixed display unit is a unit of the PRIMARY
 *     measure, so this value shows as entered), and fractions. `min`, `max`
 *     and `displayRules` are numbers in the primary's default unit: they never
 *     reach another measure;
 *   - a measure the element does not allow: null.
 */
function scopeOptions(options, measureId, unitId) {
	const opts = options || {};
	const allowed = allowedMeasures(opts);
	if (allowed.indexOf(measureId) === -1) return null;
	if (allowed.length === 1) return opts;
	const allowlist = Array.isArray(opts.units) ? opts.units : [];
	const own = allowlist.filter(id => measureOfUnit(id) === measureId);
	const scoped = {};
	if (measureId === allowed[0]) {
		// The primary: everything but the other measures and the own units, so
		// the scoped options are ONE measure's.
		for (const key of Object.keys(opts)) if (key !== 'measures' && key !== 'ownUnits') scoped[key] = opts[key];
		if (allowlist.length) scoped.units = own;
		return scoped;
	}
	const display = opts.display && typeof opts.display === 'object' && !Array.isArray(opts.display) ? opts.display : null;
	const keepDisplay = (unitMode) => {
		if (!display) return;
		const kept = {};
		for (const key of Object.keys(display)) kept[key] = display[key];
		kept.unit = unitMode;
		scoped.display = kept;
	};
	// An own unit (KPR-1109): that unit alone, its default itself, shown as
	// entered (it converts to nothing), with the display's precision and
	// fractions.
	if (isOwnUnitId(measureId)) {
		scoped.measure = measureId;
		scoped.defaultUnit = measureId;
		scoped.ownUnits = [ownUnitOf(measureId, opts)];
		keepDisplay('entered');
		if (opts.fractions === true) scoped.fractions = true;
		return scoped;
	}
	const unit = getUnit(unitId);
	scoped.measure = measureId;
	scoped.defaultUnit = unit && unit.measure === measureId ? unit.id : getMeasure(measureId).canonical;
	if (allowlist.length) scoped.units = own;
	keepDisplay(display && display.unit === 'auto' ? 'auto' : 'entered');
	if (opts.fractions === true) scoped.fractions = true;
	return scoped;
}

// ------------------------------------------------------------------ own units
//
// UNITS AN ELEMENT'S AUTHOR NAMES (KPR-1109, the owner's rulings 2026-10-22):
// a pinch of something with no built-in name, a bale, a "baker's dozen". An
// element lists them in `options.ownUnits`, at most MAX_OWN_UNITS:
//
//   { id: 'own:smidgen', singular: 'smidgen', plural: 'smidgens' }
//
// They behave as COUNT units: nothing converts to or from one, a value in one
// adds, subtracts and scales only with values in that same unit, and each is
// a measure of one unit (allowedMeasures, measureOfUnit, unitIdsOf above), so
// 2 smidgens is never 2 dashes, 2 each or 2 cups in a comparison. A value is
// { value, unit: <own id>, base } with base equal to value.
//
// THE ID is minted once, from the singular as first written (ownUnitIdFor),
// and never changes: renaming a unit relabels every value without rewriting
// any. The `own:` prefix is what keeps an own unit apart from the registry
// whatever the registry grows to, and what makes every reader that has no
// element options at hand fail CLOSED: getUnit('own:smidgen') is null, so a
// reader without the element's options sees an unknown unit, with no base.
// An own unit is its ELEMENT'S: within an element its id is the unit. Two
// elements that each named a unit "smidgen" mint the same id, but a rename
// keeps an id while its name moves on, so one card's own:smidgen may read
// "dollop" while another's still reads "smidgen". Across elements, then, two
// own units are one unit only when their ids AND their singulars (case and
// spacing aside) agree (sameOwnUnit): a formula, a driven copy and a change
// of card carry a value across only then, and a KQL literal is read on each
// card by that card's unit of its name (keepr-api utils/kql/compile.js
// ownUnitScope).
//
// THE NAMES are the element's. The parser reads them BEFORE the registry's
// spellings (parse.js), so a stored own unit keeps its meaning whatever the
// registry adds later; and a name the registry already spells is refused when
// a unit is added or renamed (ownUnitNameProblem), so typing is never
// ambiguous when it is written.

const OWN_PREFIX = 'own:';
const MAX_OWN_UNITS = 20;
const MAX_OWN_NAME = 40;
// The slug part of a minted id; a suffix "-2".."-21" can follow it.
const MAX_OWN_SLUG = 32;
const OWN_ID_RE = /^own:[a-z0-9]+(?:-[a-z0-9]+)*$/;
const MAX_OWN_ID = OWN_PREFIX.length + MAX_OWN_SLUG + 3;
// Letters (any script, with their marks), spaces, hyphens and apostrophes. No
// digit, dot, slash, comma or fraction mark: those are how the parser finds
// the number, and a name holding one could never be read back.
const OWN_NAME_RE = /^[\p{L}\p{M}](?:[\p{L}\p{M}'\u2019 -]*[\p{L}\p{M}'\u2019])?$/u;

/** True for an id of the own-unit kind (`own:` and something after it). Never throws. */
function isOwnUnitId(id) {
	return typeof id === 'string' && id.length > OWN_PREFIX.length && id.slice(0, OWN_PREFIX.length) === OWN_PREFIX;
}

/** A name as it is stored: trimmed, inner spaces collapsed, composed (NFC). '' for no text. */
function cleanOwnName(name) {
	if (typeof name !== 'string') return '';
	const text = name.replace(/\s+/g, ' ').trim();
	return typeof text.normalize === 'function' ? text.normalize('NFC') : text;
}

/** The key two names are told apart by: the stored spelling, lower-cased. */
function ownNameKey(name) {
	return cleanOwnName(name).toLowerCase();
}

/**
 * pluralOf(singular) -> the English plural a unit name takes when its author
 * gives none: "pinch" -> "pinches", "berry" -> "berries", "bale" -> "bales".
 * A name that does not end in a Latin letter keeps its spelling.
 */
function pluralOf(singular) {
	const name = cleanOwnName(singular);
	if (!name) return '';
	if (!/[A-Za-z]$/.test(name)) return name;
	if (/(?:s|x|z|ch|sh)$/i.test(name)) return name + (/[A-Z]$/.test(name) ? 'ES' : 'es');
	if (/[^aeiou]y$/i.test(name)) return name.slice(0, -1) + (/[A-Z]$/.test(name) ? 'IES' : 'ies');
	return name + (/[A-Z]$/.test(name) ? 'S' : 's');
}

/**
 * ownUnitsOf(options) -> [{ id, singular, plural }]
 * The own units an element's options name, in their order. A read rule, so
 * tolerant: an entry with no own-unit id or no singular is skipped, a second
 * entry with one id is skipped, and a missing plural reads as pluralOf. The
 * card definition gate (keepr-api utils/measurementUtils.js, settleOwnUnits)
 * refuses a malformed list before it is ever stored.
 */
function ownUnitsOf(options) {
	const list = options && Array.isArray(options.ownUnits) ? options.ownUnits : null;
	if (!list) return [];
	const out = [];
	const seen = {};
	for (const entry of list) {
		if (!entry || typeof entry !== 'object') continue;
		const id = entry.id;
		const singular = cleanOwnName(entry.singular);
		if (!isOwnUnitId(id) || !singular || Object.prototype.hasOwnProperty.call(seen, id)) continue;
		seen[id] = true;
		const plural = cleanOwnName(entry.plural) || pluralOf(singular);
		out.push({ id, singular, plural });
	}
	return out;
}

/** One own unit of an element by id, or null. */
function ownUnitOf(unitId, options) {
	if (!isOwnUnitId(unitId)) return null;
	const list = ownUnitsOf(options);
	for (const own of list) if (own.id === unitId) return own;
	return null;
}

/**
 * The element's own unit a spelling names: its id, its singular or its
 * plural, in any case and spacing. Null when it names none.
 */
function resolveOwnUnit(spelling, options) {
	if (typeof spelling !== 'string') return null;
	const trimmed = spelling.trim();
	if (!trimmed) return null;
	const key = ownNameKey(trimmed);
	for (const own of ownUnitsOf(options)) {
		if (own.id === trimmed || ownNameKey(own.singular) === key || ownNameKey(own.plural) === key) return own;
	}
	return null;
}

/**
 * An own unit in the registry's unit shape, for the readers that list units
 * (a picker, a schema): a count unit of one, converting to nothing. A fresh
 * object each call; never in UNITS.
 */
function ownUnitAsUnit(own) {
	if (!own) return null;
	return {
		id: own.id,
		measure: own.id,
		label: own.plural,
		symbol: own.singular,
		plural: own.plural,
		system: 'any',
		family: 'own',
		aliases: [own.singular, own.plural],
		convertible: false,
		autoDisplay: false,
		decreasing: false,
		joined: false,
		own: true,
		toBase: n => n,
		fromBase: b => b,
	};
}

/** A unit by id: the registry's, else an own unit of these options (in the unit shape), else null. */
function unitIn(unitId, options) {
	return getUnit(unitId) || ownUnitAsUnit(ownUnitOf(unitId, options));
}

// FNV-1a over the name's UTF-16 code units, in base 36: the id of a name
// with no Latin letter to slug (one in Cyrillic, say). Deterministic, so both repos
// mint the same id; short, because a collision only adds a suffix.
function nameHash(text) {
	let h = 0x811c9dc5;
	for (let i = 0; i < text.length; i++) {
		h ^= text.charCodeAt(i);
		h = Math.imul(h, 0x01000193) >>> 0;
	}
	return h.toString(36);
}

/**
 * ownUnitIdFor(singular, taken) -> 'own:<slug>'
 * The id a new own unit is given: its singular folded to lower-case ASCII
 * letters and digits joined by hyphens (accents dropped), at most 32 of them,
 * or 'u' and a hash of the name when no Latin letter is left; with "-2",
 * "-3"... when `taken` (ids, a list) already holds it. The one minting rule:
 * the card editor and the server call it alike.
 */
function ownUnitIdFor(singular, taken) {
	const name = cleanOwnName(singular).toLowerCase();
	const folded = typeof name.normalize === 'function' ? name.normalize('NFKD').replace(/[\u0300-\u036f]/g, '') : name;
	let slug = folded.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, MAX_OWN_SLUG).replace(/-+$/g, '');
	if (!slug) slug = 'u' + nameHash(name);
	const used = Array.isArray(taken) ? taken : [];
	const base = OWN_PREFIX + slug;
	if (used.indexOf(base) === -1) return base;
	for (let n = 2; ; n++) {
		const id = base + '-' + n;
		if (used.indexOf(id) === -1) return id;
	}
}

/**
 * ownUnitNameProblem(name, { builtIn }) -> null | { code, unit?, message }
 * Why a name cannot name an own unit, or null. `code`: 'empty', 'too_long',
 * 'characters', or 'builtin' (the parser already reads it as a built-in
 * unit, named in `unit`). `builtIn: false` skips that last question -- for a
 * unit stored unchanged since a save that allowed it, so the registry
 * growing never stops a card from saving.
 */
function ownUnitNameProblem(name, opts) {
	const text = cleanOwnName(name);
	if (!text) return { code: 'empty', message: 'needs a name' };
	if (text.length > MAX_OWN_NAME) return { code: 'too_long', message: 'is longer than ' + MAX_OWN_NAME + ' characters' };
	if (!OWN_NAME_RE.test(text)) return { code: 'characters', message: 'may hold only letters, spaces, hyphens and apostrophes' };
	if (!(opts && opts.builtIn === false)) {
		const unit = resolveUnit(text);
		if (unit) return { code: 'builtin', unit: unit.id, message: 'is already a built-in unit (' + unit.id + ')' };
	}
	return null;
}

/**
 * sameOwnUnit(a, b) -> boolean
 * Whether two own units, each { id, singular } as an element names it, are
 * one unit: the same id AND the same singular, case and spacing aside. Within
 * one element the id alone decides; across two elements a rename may have
 * moved one's name on under the id the other still names the old way.
 */
function sameOwnUnit(a, b) {
	if (!a || !b || !isOwnUnitId(a.id) || a.id !== b.id) return false;
	return ownNameKey(a.singular) === ownNameKey(b.singular);
}

// The id of the stored unit (`before`) a name names, when no entry of `raw`
// claims that id by sending it and none settled so far took it.
function storedIdNamed(before, name, raw, settled) {
	const key = ownNameKey(name);
	for (const entry of before) {
		if (!entry || typeof entry !== 'object' || !isOwnUnitId(entry.id)) continue;
		const names = [ownNameKey(entry.singular), ownNameKey(entry.plural || pluralOf(cleanOwnName(entry.singular)))];
		if (names.indexOf(key) === -1) continue;
		const claimed = raw.some(other => other && typeof other === 'object' && other.id === entry.id);
		if (!claimed && !Object.prototype.hasOwnProperty.call(settled, entry.id)) return entry.id;
	}
	return null;
}

/**
 * settleOwnUnits(raw, previous) -> { ownUnits, problem }
 *
 * An element's `ownUnits` as it is stored, or the first reason it cannot be:
 * the one rule the card definition gate and the card editor both apply.
 *   ownUnits  [{ id, singular, plural }]: names cleaned, a missing plural
 *             filled (pluralOf); a missing id is the id of the stored unit of
 *             that name (so a list re-sent by names changes nothing), else
 *             minted (ownUnitIdFor) clear of every id this list or `previous`
 *             holds, so a new unit never takes the id of one being removed;
 *   problem   null, or { code, index?, field?, name?, unit?, message }, code
 *             'shape' | 'too_many' | 'empty' | 'too_long' | 'characters' |
 *             'builtin' | 'duplicate' | 'id'.
 * `previous` is the stored list (or null): a unit whose singular and plural
 * it already holds under that id -- sent with the id, or by its names alone --
 * is not asked the built-in question again.
 */
function settleOwnUnits(raw, previous) {
	if (raw === undefined || raw === null) return { ownUnits: [], problem: null };
	if (!Array.isArray(raw)) return { ownUnits: [], problem: { code: 'shape', message: 'must be a list of units, each { singular, plural }' } };
	if (raw.length > MAX_OWN_UNITS) return { ownUnits: [], problem: { code: 'too_many', message: 'holds at most ' + MAX_OWN_UNITS + ' units' } };
	const before = Array.isArray(previous) ? previous : [];
	const stored = {};
	for (const entry of before) {
		if (entry && typeof entry === 'object' && isOwnUnitId(entry.id)) stored[entry.id] = entry;
	}
	const taken = Object.keys(stored);
	for (const entry of raw) {
		if (entry && typeof entry === 'object' && typeof entry.id === 'string' && entry.id && taken.indexOf(entry.id) === -1) taken.push(entry.id);
	}
	const out = [];
	const ids = {};
	const names = {};
	for (let i = 0; i < raw.length; i++) {
		const entry = raw[i];
		if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return { ownUnits: [], problem: { code: 'shape', index: i, message: 'each unit is { singular, plural }' } };
		if (entry.singular !== undefined && typeof entry.singular !== 'string') return { ownUnits: [], problem: { code: 'shape', index: i, field: 'singular', message: 'a unit\'s singular is text' } };
		if (entry.plural !== undefined && entry.plural !== null && typeof entry.plural !== 'string') return { ownUnits: [], problem: { code: 'shape', index: i, field: 'plural', message: 'a unit\'s plural is text' } };
		const singular = cleanOwnName(entry.singular);
		const plural = cleanOwnName(entry.plural) || pluralOf(singular);
		const sent = entry.id === undefined || entry.id === null || entry.id === '' ? null : entry.id;
		// Sent by name alone: the stored unit of that name keeps its id (a list
		// re-sent without ids changes nothing).
		const reused = sent === null ? storedIdNamed(before, singular, raw, ids) : null;
		const known = sent !== null ? sent : reused;
		const old = typeof known === 'string' && Object.prototype.hasOwnProperty.call(stored, known) ? stored[known] : null;
		const unchanged = !!old && cleanOwnName(old.singular) === singular && (cleanOwnName(old.plural) || pluralOf(cleanOwnName(old.singular))) === plural;
		const fields = [['singular', singular], ['plural', plural]];
		for (const pair of fields) {
			const problem = ownUnitNameProblem(pair[1], { builtIn: !unchanged });
			if (problem) return { ownUnits: [], problem: { code: problem.code, index: i, field: pair[0], name: pair[1], unit: problem.unit, message: '"' + (pair[1] || '') + '" ' + problem.message } };
		}
		for (const name of singular.toLowerCase() === plural.toLowerCase() ? [singular] : [singular, plural]) {
			const key = ownNameKey(name);
			if (Object.prototype.hasOwnProperty.call(names, key)) {
				return { ownUnits: [], problem: { code: 'duplicate', index: i, name, message: '"' + name + '" names two units' } };
			}
			names[key] = true;
		}
		let id = sent;
		if (id === null) {
			// Else a new id, clear of every id this list or the stored one holds.
			id = reused || ownUnitIdFor(singular, taken.concat(Object.keys(ids)));
		} else if (typeof id !== 'string' || id.length > MAX_OWN_ID || !OWN_ID_RE.test(id)) {
			return { ownUnits: [], problem: { code: 'id', index: i, message: 'the id "' + String(id) + '" is not an own-unit id (own: and lower-case letters, digits and hyphens)' } };
		}
		if (Object.prototype.hasOwnProperty.call(ids, id)) return { ownUnits: [], problem: { code: 'id', index: i, message: 'the id "' + id + '" names two units' } };
		ids[id] = true;
		out.push({ id, singular, plural });
	}
	return { ownUnits: out, problem: null };
}

/**
 * ownUnitIdIn(singular, others, previous) -> 'own:<slug>'
 * The id a new row named `singular` takes in an element's list -- the card
 * editor's question when a singular is left, and the server's for a unit sent
 * without an id (settleOwnUnits): `others`, the ids the list's other units
 * hold; `previous`, the stored list. The stored unit of that name, when no
 * other unit holds its id (one removed and added back in one draft keeps its
 * id and its values); else a new id clear of every id in `others` and
 * `previous`, so a new unit never takes a stored one's id -- a renamed one's
 * values would read as the new unit's without the question its removal asks.
 */
function ownUnitIdIn(singular, others, previous) {
	const before = Array.isArray(previous) ? previous : [];
	const used = (Array.isArray(others) ? others : []).filter(isOwnUnitId);
	const reused = storedIdNamed(before, cleanOwnName(singular), used.map(id => ({ id })), {});
	if (reused) return reused;
	const taken = used.slice();
	for (const entry of before) {
		if (entry && typeof entry === 'object' && isOwnUnitId(entry.id) && taken.indexOf(entry.id) === -1) taken.push(entry.id);
	}
	return ownUnitIdFor(singular, taken);
}

/**
 * The key a measure's values are GROUPED by when a list is sorted (KPR-1085,
 * KPR-1109): a registry measure by its id, so measures sort A to Z, and an
 * own unit after every one of them ("~" sorts after any lower-case id), A to
 * Z by id among themselves. The API's sort stages and the web's in-memory
 * sort read this one rule.
 */
function sortGroupOf(measureId) {
	if (typeof measureId !== 'string' || !measureId) return null;
	return isOwnUnitId(measureId) ? '~' + measureId : measureId;
}

module.exports = {
	MEASURES,
	MEASURE_LIST,
	UNITS,
	ALIASES,
	RATE_TIMES,
	getMeasure,
	getUnit,
	unitsOf,
	isUnitOf,
	isComposite,
	resolveUnit,
	isMeasurementValue,
	MAX_MEASURES,
	allowedMeasures,
	isMultiMeasure,
	measureOfUnit,
	unitIdsOf,
	scopeOptions,
	OWN_PREFIX,
	MAX_OWN_UNITS,
	MAX_OWN_NAME,
	isOwnUnitId,
	ownUnitsOf,
	ownUnitOf,
	resolveOwnUnit,
	ownUnitAsUnit,
	unitIn,
	pluralOf,
	ownUnitIdFor,
	ownUnitIdIn,
	ownNameKey,
	sameOwnUnit,
	ownUnitNameProblem,
	settleOwnUnits,
	sortGroupOf,
};
